import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Flag, Loader2, CheckCircle2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const REASONS = [
  { id: "not_halal", label: "Not actually halal" },
  { id: "closed", label: "Permanently closed" },
  { id: "wrong_info", label: "Wrong info (address / hours / phone)" },
  { id: "wrong_photos", label: "Misleading photos" },
  { id: "duplicate", label: "Duplicate listing" },
  { id: "other", label: "Other" },
];

export default function ReportDialog({ open, onOpenChange, restaurant }) {
  const [reason, setReason] = useState("not_halal");
  const [comment, setComment] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const reset = () => {
    setReason("not_halal");
    setComment("");
    setEmail("");
    setDone(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!restaurant) return;
    setLoading(true);
    try {
      await api.post("/reports", {
        place_id: restaurant.place_id,
        restaurant_name: restaurant.name,
        reason,
        comment: comment || undefined,
        reporter_email: email || undefined,
      });
      setDone(true);
      toast.success("Thanks for the heads-up!", {
        description: "Our team will review this report soon.",
      });
    } catch (err) {
      toast.error("Couldn't submit report", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent
        data-testid="report-dialog"
        className="sm:max-w-[440px] p-0 overflow-hidden bg-white border-[#E5E1D8] rounded-3xl"
      >
        <div className="px-6 pt-6 pb-4 bg-gradient-to-br from-[#FDFBF7] to-[#F2EDE3] border-b border-[#E5E1D8]">
          <DialogHeader className="space-y-1.5">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[#2A3B2C] flex items-center justify-center">
                <Flag className="w-4 h-4 text-white" />
              </div>
              <DialogTitle className="font-display text-xl font-medium text-[#2A3B2C]">
                Looks off?
              </DialogTitle>
            </div>
            <DialogDescription className="text-sm text-[#647466]">
              Help the community keep{" "}
              <span className="font-semibold text-[#2A3B2C]">
                {restaurant?.name || "this listing"}
              </span>{" "}
              accurate.
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="p-6">
          {done ? (
            <div className="text-center py-6" data-testid="report-success">
              <div className="mx-auto w-14 h-14 rounded-full bg-[#FFF3EE] flex items-center justify-center">
                <CheckCircle2 className="w-7 h-7 text-[#D95D39]" />
              </div>
              <h4 className="mt-3 font-display text-lg font-medium text-[#2A3B2C]">
                Thanks for flagging this
              </h4>
              <p className="mt-1 text-sm text-[#647466]">
                Reports help our admins keep AlKoncierge trustworthy. We'll review
                this within 48 hours.
              </p>
              <button
                onClick={() => onOpenChange(false)}
                data-testid="report-close-after"
                className="mt-5 inline-flex items-center justify-center gap-2 h-10 px-5 rounded-full bg-[#2A3B2C] hover:bg-[#1f2b21] text-white text-sm font-medium"
              >
                Close
              </button>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="space-y-4"
              data-testid="report-form"
            >
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.18em] font-bold text-[#647466]">
                  What's the issue?
                </Label>
                <div className="space-y-1.5">
                  {REASONS.map((r) => (
                    <label
                      key={r.id}
                      className={`flex items-center gap-2.5 px-3 py-2 rounded-xl border cursor-pointer transition-colors ${
                        reason === r.id
                          ? "border-[#D95D39] bg-[#FFF3EE]"
                          : "border-[#E5E1D8] hover:border-[#D95D39]/60"
                      }`}
                    >
                      <input
                        type="radio"
                        name="reason"
                        value={r.id}
                        checked={reason === r.id}
                        onChange={() => setReason(r.id)}
                        data-testid={`report-reason-${r.id}`}
                        className="w-4 h-4 text-[#D95D39] focus:ring-[#D95D39]"
                      />
                      <span className="text-sm text-[#2A3B2C]">{r.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.18em] font-bold text-[#647466]">
                  Add detail (optional)
                </Label>
                <Textarea
                  data-testid="report-comment"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="e.g. They serve pork on the menu, or the address has moved…"
                  rows={3}
                  maxLength={600}
                  className="border-[#E5E1D8] focus-visible:ring-[#D95D39] rounded-xl resize-none"
                />
                <div className="text-[10px] text-[#8A9A8B] text-right">
                  {comment.length}/600
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.18em] font-bold text-[#647466]">
                  Your email (optional)
                </Label>
                <Input
                  type="email"
                  data-testid="report-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="So we can follow up if needed"
                  className="border-[#E5E1D8] focus-visible:ring-[#D95D39] rounded-xl h-10"
                />
              </div>

              <div className="rounded-xl border border-[#E5E1D8] bg-[#FDFBF7] px-3 py-2.5 flex items-start gap-2 text-xs text-[#647466]">
                <ShieldCheck className="w-3.5 h-3.5 mt-0.5 text-[#2A3B2C] shrink-0" />
                <span>
                  We never share who reported what. Repeat / abusive reports may
                  be rate-limited.
                </span>
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="report-submit"
                className="w-full h-11 inline-flex items-center justify-center gap-2 rounded-full bg-[#2A3B2C] hover:bg-[#1f2b21] disabled:opacity-60 text-white text-sm font-medium transition-colors"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Flag className="w-4 h-4" />
                    Submit report
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
